import React, {useState, useEffect, useRef, useContext} from "react";
import axios from "axios";
import {useTranslation} from "react-i18next";
import {useParams, Link, useLocation} from "react-router-dom";
import {Helmet} from "react-helmet";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {toast} from "react-toastify";

import {
    FaMapMarkerAlt,
    FaRulerCombined,
    FaBuilding,
    FaEnvelope, FaStar, FaWhatsapp, FaTelegramPlane, FaFacebookMessenger,
} from "react-icons/fa";
import Logo from "../../assets/image/logo.svg";
import "../../assets/stylesheets/EstateDetails.css";
import {routes} from "../../routes";
import i18n from "i18next";
import {MdMeetingRoom} from "react-icons/md";
import {GiReceiveMoney} from "react-icons/gi";
import {ContactInfoContext} from "../additional/ContactInfoContext";
import {ReactComponent as PhoneIcon} from '../../assets/image/phone2.svg';
import {FiShare2} from "react-icons/fi";
import {IoBed} from "react-icons/io5";
import {TfiSharethis} from "react-icons/tfi";


function ContactModal({isOpen, onClose, estateId, language, onSubmit}) {
    const {t} = useTranslation(["estate"]);
    const estateMessage = t("estate-message");
    const [formData, setFormData] = useState({
        firstName: "",
        secondName: "",
        email: "",
        phoneNumber: "",
        message: estateMessage,
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
        setErrors((prev) => ({...prev, [name]: ""}));
    };

    useEffect(() => {
        // Only update if the message wasn't changed by the user
        setFormData((prevData) => ({
            ...prevData,
            message: t("estate-message"),
        }));
    }, [language]);

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = t("estate:contactForm.errors.firstName");
        if (!formData.secondName.trim()) newErrors.secondName = t("estate:contactForm.errors.secondName");
        if (!formData.email.trim()) {
            newErrors.email = t("estate:contactForm.errors.emailRequired");
        } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)) {
            newErrors.email = t("estate:contactForm.errors.emailInvalid");
        }
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = t("estate:contactForm.errors.phoneNumber");
        if (!formData.message.trim()) newErrors.message = t("estate:contactForm.errors.message");
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setIsSubmitting(true);
        try {
            await onSubmit({...formData, subject: estateId});
            setFormData({firstName: "", secondName: "", email: "", phoneNumber: "", message: ""});
            onClose();
            toast.success(t("estate:contactForm.success"), {
                position: "top-center", // 👈 Centered on screen
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                style: {
                    background: "#115772", // 👈 Your custom color
                    color: "#fff",         // Text color
                    fontWeight: "bold",
                    fontSize: "16px",
                    borderRadius: "8px",
                },
            });

        } catch (error) {
            console.error("Error sending message:", error);
            setErrors({submit: t("estate:contactForm.errors.submitFailed")});
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="estate-contact-overlay">
            <div className="estate-contact-content">
                <div className="estate-contact-header">
                    <h2 className="estate-contact-title">{t("estate:contactForm.title")}</h2>
                    <span className="estate-contact-close" onClick={onClose}>
            ×
          </span>
                </div>
                <form onSubmit={handleSubmit} className="estate-contact-form">
                    <div className="estate-contact-form-group">
                        <label className="estate-contact-form-label">{t("estate:contactForm.firstName")}</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="estate-contact-form-input"
                            placeholder={t("estate:contactForm.placeholders.firstName")}
                        />
                        {errors.firstName && <p className="estate-contact-form-error">{errors.firstName}</p>}
                    </div>
                    <div className="estate-contact-form-group">
                        <label className="estate-contact-form-label">{t("estate:contactForm.secondName")}</label>
                        <input
                            type="text"
                            name="secondName"
                            value={formData.secondName}
                            onChange={handleChange}
                            className="estate-contact-form-input"
                            placeholder={t("estate:contactForm.placeholders.secondName")}
                        />
                        {errors.secondName && <p className="estate-contact-form-error">{errors.secondName}</p>}
                    </div>
                    <div className="estate-contact-form-group">
                        <label className="estate-contact-form-label">{t("estate:contactForm.email")}</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="estate-contact-form-input"
                            placeholder={t("estate:contactForm.placeholders.email")}
                        />
                        {errors.email && <p className="estate-contact-form-error">{errors.email}</p>}
                    </div>
                    <div className="estate-contact-form-group">
                        <label className="estate-contact-form-label">{t("estate:contactForm.phoneNumber")}</label>
                        <input
                            type="text"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            className="estate-contact-form-input"
                            placeholder={t("estate:contactForm.placeholders.phoneNumber")}
                        />
                        {errors.phoneNumber && <p className="estate-contact-form-error">{errors.phoneNumber}</p>}
                    </div>
                    <div className="estate-contact-form-group">
                        <label className="estate-contact-form-label">{t("estate:contactForm.message")}</label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            className="estate-contact-form-textarea"
                            placeholder={t("estate:contactForm.placeholders.message")}
                        ></textarea>
                        {errors.message && <p className="estate-contact-form-error">{errors.message}</p>}
                    </div>
                    {errors.submit && <p className="estate-contact-form-error">{errors.submit}</p>}
                    <button type="submit" className="estate-contact-form-submit" disabled={isSubmitting}>
                        {isSubmitting ? t("estate:contactForm.submitting") : t("estate:contactForm.submit")}
                    </button>
                </form>
            </div>
        </div>
    );
}

function getRouteTypeFromBaseUrl(baseUrl, lang) {
    for (const [key, value] of Object.entries(routes)) {
        if (value[lang] === baseUrl) {
            return key;
        }
    }
    return null;
}

function EstateDetail() {
    const {t} = useTranslation(["home"]);
    const {language, baseUrl, id} = useParams();
    const [estate, setEstate] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const sliderRef = useRef(null);
    const modalSliderRef = useRef(null);
    const type = getRouteTypeFromBaseUrl(baseUrl, language);
    const {contactInfo} = useContext(ContactInfoContext);
    const location = useLocation();
    const [shareDialogContent, setShareDialogContent] = useState(null); // State for pre-constructed share dialog

    useEffect(() => {
        const fetchEstate = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_MY_API_KAY}/${type}/get?id=${id}&language=${language}`
                );
                setEstate(response.data);
            } catch (error) {
                console.error("Error fetching estate details:", error);
            }
        };
        fetchEstate();
    }, [type, id, language]);

    const formatPrice = (value, currency) => {
        if (!value) return "";
        return `${currency} ${Number(value).toLocaleString("en-US")}`;
    };

    const formatArea = (value) => {
        if (value == null) return "";
        const num = Number(value);
        return num % 1 === 0 ? `${num.toFixed(0)}` : `${num}`;
    };

    const handleContactSubmit = async (formData) => {
        await axios.post(`${process.env.REACT_APP_MY_API_KAY}/contact/real-estate`, null, {
            params: formData,
        });
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);


    // Add state for favorites
    const [favoriteIds, setFavoriteIds] = useState(() => {
        const storedFavorites = localStorage.getItem("favoriteEstates");
        return storedFavorites ? JSON.parse(storedFavorites) : [];
    });

    // Pre-construct share dialog when estate.selfUrl changes
    useEffect(() => {
        if (estate?.selfUrl) {
            const shareUrl = estate.selfUrl || "";
            setShareDialogContent(
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "12px",
                        padding: "8px",
                        width: "100%",
                    }}
                >
                    <a
                        href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer__social-icon"
                    >
                        <FaWhatsapp size={22} />
                    </a>
                    <a
                        href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer__social-icon"
                    >
                        <FaTelegramPlane size={22} />
                    </a>
                    <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer__social-icon"
                    >
                        <FaFacebookMessenger size={22} />
                    </a>
                    <button
                        className="footer__social-icon-copy"
                        onClick={() => {
                            navigator.clipboard.writeText(shareUrl).then(() => {
                                toast.success(t("estate:copied"), {
                                    position: "top-center",
                                    autoClose: 3000,
                                    hideProgressBar: true,
                                });
                            });
                        }}
                    >
                        <TfiSharethis size={22} />
                    </button>
                </div>
            );
        }
    }, [estate, language, type, id, t]);


    const handleShare = () => {
        if (!shareDialogContent) {
            console.warn("Share dialog content not ready, estate.selfUrl might be missing");
            toast.error(t("estate:shareError"), {
                position: getToastPosition(),
                autoClose: 3000,
            });
            return;
        }

        toast.info(shareDialogContent, {
            position: getToastPosition(),
            autoClose: 10000,
            closeOnClick: false,
            draggable: false,
            hideProgressBar: false,
            icon: false,
            toastId: `share-${id}`, // Unique toast ID to prevent duplicates
            style: {
                background: "#115772",
                color: "white",
                borderRadius: "10px",
                padding: "12px",
                width: window.innerWidth <= 768 ? "90vw" : "auto",
                maxWidth: "100vw",
                boxSizing: "border-box",
            },
        });
    };

    const getToastPosition = () => {
        if (window.innerWidth <= 768) {
            return "bottom-center"; // Mobile
        }
        return "top-center"; // Desktop
    };

    // Update local storage when favoriteIds changes
    useEffect(() => {
        localStorage.setItem("favoriteEstates", JSON.stringify(favoriteIds));
    }, [favoriteIds]);

    // Add toggleFavorite function
    const toggleFavorite = (id) => {
        setFavoriteIds((prev) => {
            if (prev.includes(id)) {
                return prev.filter((favId) => favId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
    };

    const modalSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        initialSlide: selectedImageIndex,
        accessibility: true,           // ✅ Enable keyboard navigation
        focusOnSelect: true,
        afterChange: (current) => {
            setSelectedImageIndex(current); // ✅ Update selected index
        },
    };

    const templateImages = [
        {
            url: "https://thumbs.dreamstime.com/b/residential-real-estate-buying-selling-renting-homes-apartments-condos-townhouses-investment-shelter-expert-guidance-318813833.jpg",
            imageKey: "default1",
            main: false,
        },
        {
            url: "https://images.moneycontrol.com/static-mcnews/2021/12/Housing-trends-4-718x435.jpg",
            imageKey: "default2",
            main: false,
        },
    ];

    if (!estate) {
        return (
            <>
                <div className="estate__detail-loading">{t("estate:loading")}</div>
                <ContactModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    estateId={id}
                    language={language}
                    onSubmit={handleContactSubmit}
                />
            </>
        );
    }

    const images = (estate.images && estate.images.length > 0? estate.images : templateImages);


    const thumbnailSettings = getThumbnailSliderSettings(images.length);

    function getThumbnailSliderSettings(imageCount) {
        return {
            slidesToShow: Math.min(imageCount, 7),
            slidesToScroll: 1,
            arrows: true,
            infinite: false,
            focusOnSelect: true,
            responsive: [
                {
                    breakpoint: 768,
                    settings: {
                        slidesToShow: Math.min(imageCount, 6),
                    },
                },
                {
                    breakpoint: 480,
                    settings: {
                        slidesToShow: Math.min(imageCount, 6),
                    },
                },
            ],
        };
    }


    const modalThumbnailSettings = getModalThumbnailSliderSettings(images.length);


    function getModalThumbnailSliderSettings(imageCount) {
        return {
            slidesToShow: Math.min(imageCount, 10),
            slidesToScroll: 1,
            arrows: imageCount > 3,
            infinite: false,
            focusOnSelect: true,
            spaceBetween: 8,
            responsive: [
                {
                    breakpoint: 1024,
                    settings: {
                        slidesToShow: Math.min(imageCount, 7),
                    },
                },
                {
                    breakpoint: 768,
                    settings: {
                        slidesToShow: Math.min(imageCount, 6),
                    },
                },
                {
                    breakpoint: 480,
                    settings: {
                        slidesToShow: Math.min(imageCount, 4),
                    },
                },
            ],
        };
    }


    const openPreviewModal = (index) => {
        setSelectedImageIndex(index);
        setIsPreviewOpen(true);
        setTimeout(() => {
            if (modalSliderRef.current) {
                modalSliderRef.current.slickGoTo(index);
            }
        }, 0);
    };

    const handleThumbnailClick = (index) => {
        setSelectedImageIndex(index);
        if (modalSliderRef.current) {
            modalSliderRef.current.slickGoTo(index);
        }
    };

    const locationParts = [];
    const addressAttr = Object.values(estate.attributes || {}).find(
        (attr) => attr.attributeType === "ADDRESS" && attr.show && attr.name && attr.name !== "null"
    );

    const address = addressAttr ? addressAttr.name : "";
    if (address) locationParts.push(address);

    const basicInfo = locationParts.length > 0 ? [{
        key: "location",
        value: locationParts.join(", "),
        icon: <FaMapMarkerAlt className="estate__detail-icon"/>,
    }] : [];

    const propertyAttrs = ["ROOMS", "FLOOR", "FLOORS", "AREA_SIZE"];
    const propertyDetails = [];
    const generalAttrs = [];
    const areaUnit = Object.values(estate.attributes || {}).find(
        (attr) => attr.attributeType === "AREA_UNIT" && attr.show && attr.name && attr.name !== "null"
    );
    Object.entries(estate.attributes || {}).forEach(([key, attr]) => {
        if (attr?.show && propertyAttrs.includes(attr.attributeType) && attr.name && attr.name !== "null") {
            let icon;
            let value = attr.name;

            if (attr.attributeType === "ROOMS") {
                icon = <IoBed className="estate__detail-icon estate__detail-icon--large"/>;
                value = `${t(`estate:propertyRooms`)} ${attr.name}`
            } else if (attr.attributeType === "AREA_SIZE") {
                icon = <FaRulerCombined className="estate__detail-icon estate__detail-icon--large"/>;
                value = `${t('estate:propertyArea')} ${formatArea(attr.name)} ${areaUnit?.name || ""}`;
            } else if (attr.attributeType === "FLOOR" || attr.attributeType === "FLOORS") {
                return;
            }
            propertyDetails.push({key, value, icon});
        } else if (attr?.show && attr.name && attr.name !== "null" && attr.attributeType === "NONE") {
            generalAttrs.push({key, value: attr.name});
        }
    });

    Object.entries(estate.attributes || {}).forEach(([key, attr]) => {
        if (attr.attributeType === "GARAGE" || attr.attributeType === "WATER" ||
            attr.attributeType === "BASEMENT" || attr.attributeType === "GAS" || attr.attributeType === "HEATING_SYSTEM") {
            generalAttrs.push({key, value: attr.name});
        }
    });

    const floorAttr = Object.values(estate.attributes || {}).find(
        (attr) => attr.attributeType === "FLOOR" && attr.show && attr.name && attr.name !== "null"
    );
    const floorsAttr = Object.values(estate.attributes || {}).find(
        (attr) => attr.attributeType === "FLOORS" && attr.show && attr.name && attr.name !== "null"
    );
    const floor = floorAttr ? floorAttr.name : "";
    const floors = floorsAttr ? floorsAttr.name : "";
    if (floor || floors) {
        const floorValue = [floor, floors].filter(Boolean).join("/");
        const finalFloor = `${t(`estate:propertyFloor`)} ${floorValue}`
        propertyDetails.push({
            key: "Floor/Floors",
            value: finalFloor,
            icon: <FaBuilding className="estate__detail-icon estate__detail-icon--large"/>,
        });
    }
    const priceAttr = Object.values(estate.attributes || {}).find(
        (attr) => attr.attributeType === "PRICE" && attr.show && attr.name && attr.name !== "null"
    );


    return (
        <div className="estate-detail-page">
            <Helmet>
                <title>{estate.title || t("estate:estateDetailTitle")}</title>
                <meta name="description" content={estate.description || t("estate:estateDetailDescribe")}/>
                <link rel="canonical" href={`https://sievn.am/${language}/${type}/${id}`}/>
                <meta property="og:title" content={estate.title || t("estate:estateDetailTitle")}/>
                <meta property="og:site_name" content="sievn.am"/>
                <meta property="og:url" content={`https://sievn.am/${language}/${type}/${id}`}/>
                <meta property="og:description" content={estate.description || t("estate:estateDetailDescribe")}/>
                <meta property="og:image" content={estate.images?.find((img) => img.imageOrder===0)?.url || Logo}/>
                <meta property="og:image:width" content="1200"/>
                <meta property="og:image:height" content="630"/>
            </Helmet>
            <section className="estate__detail">
                <div className="breadcrumb">
                    <Link to={`/${language}`}>{t("home")}</Link> {"> "}
                    <Link to={`/${language}/${t(`estate:${type}-url`)}`}>{t(`estate:${type}-title`)}</Link> {"> "}
                    <span>{estate.title}</span>
                </div>
                <div className="estate__detail-container">
                    <div className="estate__detail-left">
                        <div className="estate__detail-carousel">
                            {estate.exclusive && (
                                <span className="estate__exclusive-badge">{t("estate:exclusive-title")}</span>
                            )}

                            {estate.crmNumber && (
                                <span className={`estate__crm-number ${!estate.exclusive ? 'estate__crm-number--top' : ''}`}>
                                          {t("estate:estate.codeLabel")}  {estate.crmNumber}
                                        </span>
                            )}

                            {estate.type === 'business' && (
                                <span className="estate__type-badge"> {t("estate:business-title")}
                                    {estate.working && ` - ${t("estate:working-title")}`}
                                </span>
                            )}

                            <Slider ref={sliderRef} {...sliderSettings}>
                                {images.map((img, index) => (
                                    <div key={img.imageKey}>
                                        <img
                                            src={img.url}
                                            alt={estate.title}
                                            className="estate__detail-image"
                                            onClick={() => openPreviewModal(index)}
                                            style={{cursor: "pointer"}}
                                        />
                                    </div>
                                ))}
                            </Slider>
                            <button
                                className={`estate__favorite-icon 
                                ${favoriteIds.includes(estate.id) ? "estate__favorite-icon--active" : ""}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    toggleFavorite(estate.id);
                                }}
                            >
                                <FaStar/>
                            </button>
                        </div>

                        <div className="estate__detail-carousel">

                            <Slider {...thumbnailSettings} className="estate__detail-thumbnails">
                                {images.map((img, index) => (
                                    <div key={img.imageKey}>
                                        <img
                                            src={img.thumbnailUrl || img.url}
                                            alt={`Thumbnail ${index + 1}`}
                                            className="estate__detail-thumbnail"
                                            onClick={() => sliderRef.current.slickGoTo(index)}
                                            style={{cursor: "pointer"}}
                                        />
                                    </div>
                                ))}
                            </Slider>
                        </div>

                        {estate.description && <h2 className="estate__detail-description-title">{t(`estate:information`)}</h2>}
                        {estate.description && <p className="estate__detail-description">{estate.description}</p>}
                    </div>
                    <div className="estate__detail-right">
                        {estate.crmNumber && (
                            <h2 className="estate__detail-code">
                                {t('estate:estate.codeLabel')} {estate.crmNumber}
                            </h2>
                        )}
                        {estate.title && <h2 className="estate__detail-title">{estate.title}</h2>}

                        {estate.description && <h2 className="estate__detail-description-title-mobile">{t(`estate:information`)}</h2>}
                        {estate.description &&
                            <p className="estate__detail-description-mobile">{estate.description}</p>}

                        <div className="estate__detail-price-container">
                            <p className="estate__detail-price">{formatPrice(priceAttr?.name, estate.currency)}</p>

                            <div className="estate__card-phone-wrapper">
                                <PhoneIcon className="phone-icon" style={{fill: '#358a9e'}}/>

                                <a href={`tel:${contactInfo[`telPhone_${type}`]}`} className="estate__card-phone">
                                    {contactInfo[`telPhone_${type}`] || t("phone")}
                                </a>
                            </div>

                            <FaEnvelope
                                className="estate__detail-contact-icon"
                                onClick={() => setIsModalOpen(true)}
                                title={t("estate:contactForm.buttonTooltip")}
                            />

                            <FiShare2
                                className="estate__detail-contact-icon"
                                onClick={handleShare}
                                title={t("estate:share")}
                            />

                        </div>
                        {basicInfo.length > 0 && (
                            <div className="estate__detail-section">
                                <h3 className="estate__detail-section-title">{t("estate:basicInfo")}</h3>
                                <p className="estate__card-type">
                                    <GiReceiveMoney className="estate__card-icon"/>
                                    {estate.type === 'business' ? (
                                        Object.values(estate.attributes || {}).some(
                                            attr => attr.show && attr.attributeType === 'RENT_TYPE'
                                        ) ? (
                                            t('estate:rent-title')
                                        ) : (
                                            t('estate:sales-title')
                                        )
                                    ) : (
                                        t(`estate:${estate.type}-title`)
                                    )}

                                    {(estate.type === 'rent' ||
                                        (estate.type === 'business' && Object.values(estate.attributes || {}).some(
                                            attr => attr.show && attr.attributeType === 'RENT_TYPE'
                                        ))
                                    ) && (
                                        <>
                                            {' - '}
                                            {Object.entries(estate.attributes || {})
                                                .filter(
                                                    ([_, attr]) =>
                                                        attr.show && attr.attributeType === 'RENT_TYPE'
                                                )
                                                .map(([_, attr]) => attr.name)
                                                .filter(value => value != null && value !== '')
                                                .join(', ')}
                                        </>
                                    )}

                                    {estate.type === 'business' && (
                                        <>
                                            {' - '}
                                            {t('estate:business-title')}
                                        </>
                                    )}
                                </p>
                                <div className="estate__detail-info estate__detail-info--horizontal--one">
                                    {basicInfo.map(({key, value, icon}) => (
                                        <p key={key} className="estate__detail-field">
                                            {icon}
                                            {value}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                        {propertyDetails.length > 0 && (
                            <>
                                <hr className="estate__detail-divider"/>
                                <div className="estate__detail-section">
                                    <h3 className="estate__detail-section-title">{t("estate:propertyDetails")}</h3>
                                    <div className="estate__detail-info estate__detail-info--horizontal">
                                        {propertyDetails.map(({key, value, icon}) => (
                                            <p key={key} className="estate__detail-field estate__detail-field--large">
                                                {icon}
                                                {value}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                        {generalAttrs.length > 0 && (
                            <>
                                <hr className="estate__detail-divider"/>
                                <div className="estate__detail-section">
                                    <h3 className="estate__detail-section-title">{t("estate:generalAttributes")}</h3>
                                    <div className="estate__detail-info estate__detail-info--general">
                                        {generalAttrs.map(({key, value, icon}, index) => (
                                            <div key={key} className="estate__detail-general-item">
                                                <p className="estate__detail-field estate__detail-field--general">
                                                    {icon}
                                                    <span className="estate__detail-key">{key}:</span>
                                                    <span className="estate__detail-value">{value}</span>
                                                </p>
                                                {index < generalAttrs.length - 1 && (
                                                    <hr className="estate__detail-general-divider"/>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {isPreviewOpen && (
                <div className="modal-overlay modal-slider">
                    <div className="modal-content-space">
                        <button
                            className="close-button-space"
                            onClick={() => setIsPreviewOpen(false)}
                            style={{position: "absolute", top: "20px", right: "20px", zIndex: 1001}}
                        >
                            ×
                        </button>
                        <div className="modal-carousel-container">
                            <Slider ref={modalSliderRef} {...modalSliderSettings}>
                                {images.map((img) => (
                                    <div key={img.imageKey}>
                                        <img
                                            src={img.url}
                                            alt={estate.title}
                                            className="modal-main-image"
                                        />
                                    </div>
                                ))}
                            </Slider>
                            <Slider {...modalThumbnailSettings} className="modal-thumbnails">
                                {images.map((img, index) => (
                                    <div key={img.imageKey}>
                                        <img
                                            src={img.thumbnailUrl || img.url}
                                            alt={`Thumbnail ${index + 1}`}
                                            className={`modal-thumbnail ${selectedImageIndex === index ? 'modal-thumbnail--active' : ''}`}
                                            onClick={() => handleThumbnailClick(index)}
                                            style={{cursor: "pointer"}}
                                        />
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    </div>
                </div>
            )}

            <ContactModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                estateId={id}
                language={language}
                onSubmit={handleContactSubmit}
            />
        </div>
    );
}

export default EstateDetail;